import { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ArrowRight, Briefcase, Users, Eye, EyeOff, Wifi, WifiOff, RefreshCcw } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/api";
import { useEffect } from "react";

function ConnectionBanner() {
    const [status, setStatus] = useState("checking"); // checking | ok | fail
    const check = async () => {
        setStatus("checking");
        try {
            await api.get("/health", { timeout: 6000 });
            setStatus("ok");
        } catch {
            setStatus("fail");
        }
    };
    useEffect(() => { check(); }, []);

    if (status === "ok") return null;
    if (status === "checking") {
        return (
            <div data-testid="conn-banner" className="mb-4 flex items-center gap-2 rounded-lg border border-[var(--border-soft)] px-3 py-2 text-xs text-[var(--text-secondary)]">
                <Wifi size={14} /> Checking connection…
            </div>
        );
    }
    return (
        <div data-testid="conn-banner-fail" className="mb-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
            <WifiOff size={16} className="mt-0.5 shrink-0" />
            <div className="flex-1">
                <div className="font-semibold">Can't reach SCS servers</div>
                <div className="text-xs mt-0.5">
                    Check your Wi-Fi or mobile signal. If you're on a work network, another Wi-Fi may work.
                </div>
            </div>
            <button onClick={check} data-testid="conn-retry-btn"
                    className="btn-ghost !p-1.5 shrink-0" aria-label="Try again">
                <RefreshCcw size={14} />
            </button>
        </div>
    );
}

export default function LoginPage() {
    const { user, loading, login, register } = useAuth();
    const navigate = useNavigate();
    const [mode, setMode] = useState("login");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [name, setName] = useState("");
    const [role, setRole] = useState("operative");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    if (loading || user === null) return null;
    if (user) return <Navigate to={user.role === "manager" ? "/manager" : "/operative"} replace />;

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true); setError("");
        const cleanEmail = (email || "").trim();
        const cleanPassword = password || "";
        const res = mode === "login"
            ? await login(cleanEmail, cleanPassword)
            : await register({ email: cleanEmail, password: cleanPassword, name: name || cleanEmail.split("@")[0], role });
        setBusy(false);
        if (!res.ok) {
            setError(res.error);
            toast.error(res.error);
            return;
        }
        toast.success(`Welcome, ${res.user.name}`);
        navigate(res.user.role === "manager" ? "/manager" : "/operative");
    };

    // quickFill kept as a helper (unused after demo buttons were removed for production).
    const quickFill = (_) => {};

    return (
        <div className="min-h-screen grid grid-cols-1 lg:grid-cols-5 bg-[var(--bg)]">
            <aside className="hidden lg:flex lg:col-span-2 relative flex-col justify-between p-10 text-white grain-bg"
                   style={{ backgroundColor: "var(--pine)" }}>
                <div className="flex items-center gap-3 relative z-10">
                    <img
                        src="/scs-icon-512.png"
                        alt="SCS"
                        className="h-14 w-14 rounded-2xl object-cover ring-1 ring-white/10"
                    />
                    <div className="leading-tight">
                        <div className="font-display text-2xl font-semibold tracking-tight">
                            SCS
                        </div>
                        <div className="text-[10px] uppercase tracking-[0.28em] font-semibold" style={{ color: "var(--terracotta)" }}>
                            Simple · Clear · Systems
                        </div>
                    </div>
                </div>

                <div className="relative z-10 max-w-md">
                    <h1 className="font-display text-4xl xl:text-5xl font-bold leading-tight tracking-tight">
                        Every job. Every team. On the same page.
                    </h1>
                    <p className="mt-6 text-base opacity-80 leading-relaxed">
                        A shared workspace for managers and their teams on the go — schedule, dispatch, and share on-site notes in real time.
                    </p>
                    <div className="mt-10 space-y-3 text-sm">
                        <div className="flex items-center gap-3 opacity-90">
                            <Briefcase size={16} /> Assign jobs with checklists & priority
                        </div>
                        <div className="flex items-center gap-3 opacity-90">
                            <Users size={16} /> Comment & upload photos on any job
                        </div>
                    </div>
                </div>

                <div className="relative z-10 text-xs opacity-60">
                    © SCS — built for field service teams.
                </div>
            </aside>

            <main className="lg:col-span-3 flex items-center justify-center p-6 sm:p-10">
                <div className="w-full max-w-md animate-in-up">
                    <div className="flex items-center gap-3 lg:hidden mb-8">
                        <img
                            src="/scs-icon-512.png"
                            alt="SCS"
                            className="h-12 w-12 rounded-2xl object-cover"
                        />
                        <div className="leading-tight">
                            <div className="font-display text-lg font-semibold">
                                SCS
                            </div>
                            <div className="text-[10px] uppercase tracking-[0.28em] font-semibold text-[var(--terracotta)]">
                                Simple · Clear · Systems
                            </div>
                        </div>
                    </div>

                    <ConnectionBanner />

                    <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight" data-testid="auth-heading">
                        {mode === "login" ? "Sign in to your crew" : "Create your account"}
                    </h2>
                    <p className="mt-2 text-[var(--text-secondary)] text-sm">
                        {mode === "login"
                            ? "Welcome back — pick up where you left off."
                            : "Start scheduling jobs and coordinating your team."}
                    </p>

                    <div className="mt-2 flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[var(--terracotta)] font-semibold">
                        <span>Specialist Cleaning Services</span>
                    </div>

                    <form onSubmit={submit} className="mt-6 space-y-4">
                        {mode === "register" && (
                            <>
                                <div>
                                    <label className="text-xs uppercase tracking-[0.18em] text-[var(--text-muted)] font-medium">Full name</label>
                                    <input data-testid="name-input" className="input-field mt-1"
                                           value={name} onChange={e => setName(e.target.value)} required />
                                </div>
                                <div>
                                    <label className="text-xs uppercase tracking-[0.18em] text-[var(--text-muted)] font-medium">I am a</label>
                                    <div className="mt-1 grid grid-cols-2 gap-2">
                                        {["operative","manager"].map(r => (
                                            <button key={r} type="button"
                                                    data-testid={`role-${r}-btn`}
                                                    onClick={() => setRole(r)}
                                                    className={`rounded-full py-2 text-sm border transition-colors ${
                                                        role === r
                                                            ? "bg-[var(--pine)] text-white border-[var(--pine)]"
                                                            : "bg-white text-[var(--text-primary)] border-[var(--border-soft)] hover:border-[var(--pine)]"
                                                    }`}>
                                                {r === "manager" ? "Manager" : "Operative"}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </>
                        )}
                        <div>
                            <label className="text-xs uppercase tracking-[0.18em] text-[var(--text-muted)] font-medium">
                                {mode === "login" ? "Email or login id" : "Email"}
                            </label>
                            <input data-testid="email-input"
                                   type={mode === "login" ? "text" : "email"}
                                   inputMode="email"
                                   autoCapitalize="none"
                                   autoCorrect="off"
                                   spellCheck={false}
                                   className="input-field mt-1"
                                   value={email} onChange={e => setEmail(e.target.value)}
                                   autoComplete="username" required />
                            {mode === "login" && (
                                <p className="text-[11px] text-[var(--text-muted)] mt-1.5">
                                    Phone-only members: enter the login id your manager sent you (looks like <span className="font-mono">member-…@scs.local</span>).
                                </p>
                            )}
                        </div>
                        <div>
                            <label className="text-xs uppercase tracking-[0.18em] text-[var(--text-muted)] font-medium">Password</label>
                            <div className="relative mt-1">
                                <input data-testid="password-input"
                                       type={showPassword ? "text" : "password"}
                                       className="input-field pr-11"
                                       value={password} onChange={e => setPassword(e.target.value)}
                                       autoComplete={mode === "login" ? "current-password" : "new-password"}
                                       required minLength={6} />
                                <button type="button"
                                        data-testid="toggle-password-btn"
                                        onClick={() => setShowPassword(v => !v)}
                                        aria-label={showPassword ? "Hide password" : "Show password"}
                                        className="absolute inset-y-0 right-2 flex items-center justify-center h-full w-8 text-[var(--text-muted)] hover:text-[var(--pine)] transition-colors">
                                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                </button>
                            </div>
                            {mode === "login" && (
                                <div className="mt-2 text-right">
                                    <Link data-testid="forgot-password-link" to="/forgot-password"
                                          className="text-xs text-[var(--text-secondary)] hover:text-[var(--pine)] hover:underline">
                                        Forgotten your password?
                                    </Link>
                                </div>
                            )}
                        </div>

                        {error && (
                            <div data-testid="auth-error" className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                                {error}
                            </div>
                        )}

                        <button data-testid="auth-submit-btn" type="submit" disabled={busy}
                                className="btn-primary w-full flex items-center justify-center gap-2">
                            {busy ? "Working…" : mode === "login" ? "Sign in" : "Create account"}
                            {!busy && <ArrowRight size={16} />}
                        </button>
                    </form>

                    <div className="mt-6 text-sm text-[var(--text-secondary)]">
                        {mode === "login" ? (
                            <>New to SCS?{" "}
                                <button data-testid="switch-register-btn" className="text-[var(--pine)] font-medium hover:underline"
                                        onClick={() => setMode("register")}>Create an account</button>
                            </>
                        ) : (
                            <>Already have an account?{" "}
                                <button data-testid="switch-login-btn" className="text-[var(--pine)] font-medium hover:underline"
                                        onClick={() => setMode("login")}>Sign in</button>
                            </>
                        )}
                    </div>
                </div>
            </main>
        </div>
    );
}
